import React, { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import useResponsiveQuery from 'utils/useResponsiveQuery';
import { SUBSCRIPTION_PLANS_PAGE } from 'components/constants';
import Clients from '../Clients';

type TypewriterPhase = 'type' | 'hold' | 'gap';

const getTypingDelay = (character: string) => {
  const naturalVariation = 24 + Math.random() * 34;

  if (/[,.!?—]/.test(character)) return naturalVariation + 90 + Math.random() * 80;
  if (character === ' ') return naturalVariation * 0.72;

  return naturalVariation;
};

const prompts = [
  'Create an 8-question mortgage advisor in our bank’s brand and language, then improve it through automated A/B testing.',
  'Launch an on-brand digital magazine with stories, video, profiles and sponsor integrations.',
  'Build a real-time storm tracker for our news homepage with live status, maps and an evolving editorial story.',
  'Build a Tax Personality quiz that matches every customer with the right product and captures first-party data.',
  'Build an insurance advisor that guides each customer to the right coverage path and sends declared needs to our data stack.',
  'Turn our annual impact data into a branded interactive story with live charts, key insights and localized versions.',
];

const mobilePrompts = [
  'Build an A/B-tested mortgage advisor.',
  'Launch an immersive digital magazine.',
  'Build a real-time storm tracker.',
  'Create a personalized product quiz.',
  'Build a personalized insurance advisor.',
  'Turn impact data into a visual story.',
];

const productHighlights = [
  {
    title: 'AI speed. Designer control.',
    description: 'Create with a prompt inside your approved system, then perfect every detail in the studio.',
  },
  {
    title: 'Enterprise control',
    description: 'Manage teams, brands, markets, roles, approvals and SSO across every product.',
  },
  {
    title: 'Personalized journeys',
    description: 'Give every user a tailored experience that drives higher engagement.',
  },
  {
    title: 'Your data. Better performance.',
    description: 'Keep data in your stack, spot flow drop-offs and optimize with AI-powered A/B testing.',
  },
];

function HeadlineSection() {
  const isMobile = useResponsiveQuery();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [prompt, setPrompt] = useState('');
  const [ghostText, setGhostText] = useState('');
  const [animatedPrompt, setAnimatedPrompt] = useState(prompts[0]);

  const enterpriseParagraph =
    'AI makes digital creation accessible, but for enterprises, building is only the beginning. Storycards is the enterprise operating layer for managing experiences across teams at scale, maintaining security and brand consistency, and turning every interaction into actionable data.';
  const subtitle =
    'Give your teams one governed platform to build, manage and scale pixel-perfect, on-brand interactive experiences. Turn every interaction into first-party data that continuously improves engagement and performance.';

  useEffect(() => {
    let timer: number | undefined;
    let phraseIndex = 0;
    let characterIndex = 0;
    let phase: TypewriterPhase = 'type';
    let cancelled = false;
    const displayedPrompts = isMobile ? mobilePrompts : prompts;

    if (prompt) {
      setGhostText('');
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setGhostText(displayedPrompts[0]);
      setAnimatedPrompt(prompts[0]);
      return undefined;
    }

    const schedule = (delay: number) => {
      timer = window.setTimeout(tick, delay);
    };

    const tick = () => {
      if (cancelled) return;

      const currentPrompt = displayedPrompts[phraseIndex];
      const characters = Array.from(currentPrompt);
      setAnimatedPrompt(prompts[phraseIndex]);

      if (phase === 'type') {
        if (characterIndex < characters.length) {
          characterIndex += 1;
          setGhostText(characters.slice(0, characterIndex).join(''));
          schedule(getTypingDelay(characters[characterIndex - 1]));
        } else {
          phase = 'hold';
          schedule(2000);
        }
        return;
      }

      if (phase === 'hold') {
        setGhostText('');
        phase = 'gap';
        schedule(420);
        return;
      }

      phraseIndex = (phraseIndex + 1) % displayedPrompts.length;
      characterIndex = 0;
      phase = 'type';
      schedule(80);
    };

    setGhostText('');
    schedule(180);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [isMobile, prompt]);

  const openStorycards = (value: string) => {
    const url = new URL(SUBSCRIPTION_PLANS_PAGE);
    url.searchParams.set('prompt', value);
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    openStorycards(prompt.trim() || animatedPrompt);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      openStorycards(prompt.trim() || animatedPrompt);
    }
  };

  return (
    <section className={styles.headlineSection}>
      <div className={styles.intro}>
        <div className={styles.textTop}>
          <h1 className={styles.title}>
            <span>Create anything.</span>
            <span>Collect everything.</span>
          </h1>
          <p className={styles.leadIn}>Built for enterprise.</p>
          <p className={styles.subtitle}>{subtitle}</p>

          <form className={styles.composer} onSubmit={handleSubmit}>
            <label className={styles.visuallyHidden} htmlFor="storycards-product-prompt">
              Describe the digital product you want to build
            </label>
            <div className={styles.composerField}>
              <textarea
                ref={textareaRef}
                id="storycards-product-prompt"
                rows={1}
                maxLength={2000}
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                spellCheck
              />
              {!prompt && (
                <p className={styles.composerGhost} aria-hidden="true">
                  {ghostText}
                </p>
              )}
              <button className={styles.composerSend} type="submit">
                <span>Create</span>
                <svg aria-hidden="true" viewBox="0 0 18 18">
                  <path d="M3.75 9h10.5M10 4.75 14.25 9 10 13.25" />
                </svg>
              </button>
            </div>
          </form>

          <div className={styles.productHighlights} aria-label="Storycards platform capabilities">
            {productHighlights.map((highlight) => (
              <div className={styles.productHighlight} key={highlight.title}>
                <strong>{highlight.title}</strong>
                <span>{highlight.description}</span>
              </div>
            ))}
          </div>

        </div>
      </div>

      <Clients />

      <section className={styles.productStage} aria-labelledby="enterprise-operation-title">
        <div className={styles.stageInner}>
          <div className={styles.enterpriseIntro}>
            <h2 id="enterprise-operation-title">
              <span>Building is easy.</span>
              <span>Operating at enterprise</span>
              <span>scale is not.</span>
            </h2>
            <p>{enterpriseParagraph}</p>
          </div>
        </div>
      </section>
    </section>
  );
}

export default HeadlineSection;
